import { Navigate } from "react-router-dom";
import PageTitle from "../page-header/PageHeader";
import {
	Card,
	Table,
	Tag,
	Statistic,
	Row,
	Col,
	Select,
	Progress,
	Alert,
	Space,
	Typography,
} from "antd";
import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import {
	CheckCircleOutlined,
	WarningOutlined,
	TrophyOutlined,
	UserOutlined,
	RiseOutlined,
	BookOutlined,
	FileTextOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { Option } = Select;

const WorkforceSkillsAnalysis = () => {
	const dispatch = useDispatch();
	const isLogged = Boolean(localStorage.getItem("isLogged"));
	const staffList = useSelector((state) => state.users?.list || []);
	const [selectedCategory, setSelectedCategory] = useState("All");

	const [skillsData, setSkillsData] = useState(() => {
		const stored = localStorage.getItem("employeeSkills");
		if (stored) {
			try {
				return JSON.parse(stored);
			} catch (error) {
				return [];
			}
		}
		return [];
	});

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	// Calculate comprehensive skills analysis
	const analysis = useMemo(() => {
		const filteredSkills = selectedCategory === "All"
			? skillsData
			: skillsData.filter((s) => s.skillCategory === selectedCategory);

		if (filteredSkills.length === 0) return null;

		const activeSkills = filteredSkills.filter((s) => s.status === "Active" || s.status === "In Progress");

		// Overall statistics
		const totalSkills = activeSkills.length;
		const uniqueEmployees = new Set(activeSkills.map((s) => s.employeeId)).size;
		const totalEmployees = staffList.length;
		const skillsCoverage = totalEmployees > 0 ? (uniqueEmployees / totalEmployees) * 100 : 0;

		// Average rating
		const avgRating =
			activeSkills.length > 0
				? activeSkills.reduce((sum, s) => sum + (s.currentRating || 0), 0) / activeSkills.length
				: 0;

		// Skills by category
		const byCategory = {};
		activeSkills.forEach((skill) => {
			const category = skill.skillCategory || "Other";
			if (!byCategory[category]) {
				byCategory[category] = {
					category,
					count: 0,
					avgRating: 0,
					totalRating: 0,
				};
			}
			byCategory[category].count++;
			byCategory[category].totalRating += skill.currentRating || 0;
		});

		Object.keys(byCategory).forEach((category) => {
			byCategory[category].avgRating =
				byCategory[category].totalRating / byCategory[category].count;
		});

		// Proficiency level distribution
		const proficiencyDistribution = {
			Beginner: 0,
			Intermediate: 0,
			Advanced: 0,
			Expert: 0,
		};

		activeSkills.forEach((skill) => {
			const level = skill.proficiencyLevel || "Beginner";
			if (proficiencyDistribution.hasOwnProperty(level)) {
				proficiencyDistribution[level]++;
			}
		});

		// Most common skills
		const skillFrequency = {};
		activeSkills.forEach((skill) => {
			const skillName = skill.skillName;
			if (!skillFrequency[skillName]) {
				skillFrequency[skillName] = {
					skillName,
					count: 0,
					avgRating: 0,
					totalRating: 0,
					category: skill.skillCategory,
				};
			}
			skillFrequency[skillName].count++;
			skillFrequency[skillName].totalRating += skill.currentRating || 0;
		});

		Object.keys(skillFrequency).forEach((skillName) => {
			skillFrequency[skillName].avgRating =
				skillFrequency[skillName].totalRating / skillFrequency[skillName].count;
		});

		const mostCommonSkills = Object.values(skillFrequency)
			.sort((a, b) => b.count - a.count)
			.slice(0, 10);

		// Top skilled employees
		const employeeSkills = {};
		activeSkills.forEach((skill) => {
			const empId = skill.employeeId;
			if (!employeeSkills[empId]) {
				employeeSkills[empId] = {
					employeeId: empId,
					employeeName: skill.employeeName,
					skills: [],
					totalSkills: 0,
					avgRating: 0,
					certifications: 0,
				};
			}
			employeeSkills[empId].skills.push(skill);
			employeeSkills[empId].totalSkills++;
			if (skill.certificationName) {
				employeeSkills[empId].certifications++;
			}
		});

		Object.keys(employeeSkills).forEach((empId) => {
			const emp = employeeSkills[empId];
			emp.avgRating =
				emp.skills.reduce((sum, s) => sum + (s.currentRating || 0), 0) / emp.skills.length;
		});

		const topSkilledEmployees = Object.values(employeeSkills)
			.sort((a, b) => {
				// Sort by total skills first, then by average rating
				if (b.totalSkills !== a.totalSkills) {
					return b.totalSkills - a.totalSkills;
				}
				return b.avgRating - a.avgRating;
			})
			.slice(0, 10);

		// Skills gaps (employees with low ratings)
		const skillsGaps = activeSkills
			.filter((s) => (s.currentRating || 0) < 3.0)
			.map((s) => ({
				employeeName: s.employeeName,
				skillName: s.skillName,
				category: s.skillCategory,
				currentRating: s.currentRating,
				targetRating: s.targetRating || 0,
				gap: (s.targetRating || 0) - (s.currentRating || 0),
			}))
			.sort((a, b) => b.gap - a.gap)
			.slice(0, 10);

		// Certification statistics
		const certifiedSkills = activeSkills.filter((s) => s.certificationName);
		const certificationRate =
			activeSkills.length > 0 ? (certifiedSkills.length / activeSkills.length) * 100 : 0;

		// Skills growth (skills added in last 6 months)
		const now = dayjs();
		const monthlyGrowth = [];
		for (let i = 5; i >= 0; i--) {
			const monthStart = now.subtract(i, "month").startOf("month");
			const monthEnd = now.subtract(i, "month").endOf("month");
			const monthSkills = activeSkills.filter((s) => {
				const acquiredDate = s.acquiredDate ? dayjs(s.acquiredDate) : dayjs(s.assessmentDate);
				return acquiredDate.isAfter(monthStart) && acquiredDate.isBefore(monthEnd);
			});
			monthlyGrowth.push({
				month: monthStart.format("MMM YYYY"),
				count: monthSkills.length,
			});
		}

		// Category breakdown
		const categoryBreakdown = Object.values(byCategory)
			.sort((a, b) => b.count - a.count)
			.map((cat) => ({
				category: cat.category,
				count: cat.count,
				avgRating: cat.avgRating.toFixed(2),
				percentage: ((cat.count / totalSkills) * 100).toFixed(1),
			}));

		return {
			totalSkills,
			uniqueEmployees,
			totalEmployees,
			skillsCoverage: skillsCoverage.toFixed(1),
			avgRating: avgRating.toFixed(2),
			proficiencyDistribution,
			mostCommonSkills,
			topSkilledEmployees,
			skillsGaps,
			certificationRate: certificationRate.toFixed(1),
			certifiedSkills: certifiedSkills.length,
			monthlyGrowth,
			categoryBreakdown,
		};
	}, [skillsData, selectedCategory, staffList]);

	const mostCommonSkillsColumns = [
		{
			title: "Rank",
			key: "rank",
			render: (_, __, index) => (
				<Tag color={index === 0 ? "gold" : index === 1 ? "default" : index === 2 ? "orange" : "blue"}>
					#{index + 1}
				</Tag>
			),
		},
		{
			title: "Skill Name",
			dataIndex: "skillName",
			key: "skillName",
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
		},
		{
			title: "Employees",
			dataIndex: "count",
			key: "count",
			align: "center",
		},
		{
			title: "Avg Rating",
			dataIndex: "avgRating",
			key: "avgRating",
			render: (rating) => (
				<Tag color={rating >= 4 ? "green" : rating >= 3 ? "blue" : "orange"}>
					{rating.toFixed(2)}/5
				</Tag>
			),
		},
	];

	const topSkilledColumns = [
		{
			title: "Rank",
			key: "rank",
			render: (_, __, index) => (
				<Tag color={index === 0 ? "gold" : index === 1 ? "default" : index === 2 ? "orange" : "blue"}>
					#{index + 1}
				</Tag>
			),
		},
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "Total Skills",
			dataIndex: "totalSkills",
			key: "totalSkills",
			align: "center",
		},
		{
			title: "Avg Rating",
			dataIndex: "avgRating",
			key: "avgRating",
			render: (rating) => (
				<Tag color={rating >= 4 ? "green" : rating >= 3 ? "blue" : "orange"}>
					{rating.toFixed(2)}/5
				</Tag>
			),
		},
		{
			title: "Certifications",
			dataIndex: "certifications",
			key: "certifications",
			align: "center",
			render: (count) => (
				<Tag color="purple" icon={<FileTextOutlined />}>
					{count}
				</Tag>
			),
		},
	];

	const skillsGapsColumns = [
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "Skill",
			dataIndex: "skillName",
			key: "skillName",
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
		},
		{
			title: "Current Rating",
			dataIndex: "currentRating",
			key: "currentRating",
			render: (rating) => (
				<Tag color="red">{rating.toFixed(1)}/5</Tag>
			),
		},
		{
			title: "Target Rating",
			dataIndex: "targetRating",
			key: "targetRating",
			render: (rating) => (rating > 0 ? `${rating.toFixed(1)}/5` : "-"),
		},
		{
			title: "Gap",
			dataIndex: "gap",
			key: "gap",
			render: (gap) => (
				<Tag color={gap > 1 ? "red" : "orange"}>
					{gap > 0 ? `+${gap.toFixed(1)}` : gap.toFixed(1)}
				</Tag>
			),
		},
	];

	const categoryColumns = [
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
		},
		{
			title: "Skills Count",
			dataIndex: "count",
			key: "count",
			align: "center",
		},
		{
			title: "Percentage",
			dataIndex: "percentage",
			key: "percentage",
			render: (percentage) => `${percentage}%`,
		},
		{
			title: "Avg Rating",
			dataIndex: "avgRating",
			key: "avgRating",
			render: (rating) => (
				<Tag color={parseFloat(rating) >= 4 ? "green" : parseFloat(rating) >= 3 ? "blue" : "orange"}>
					{rating}/5
				</Tag>
			),
		},
		{
			title: "Distribution",
			key: "distribution",
			render: (_, record) => (
				<Progress
					percent={parseFloat(record.percentage)}
					strokeColor={parseFloat(record.avgRating) >= 4 ? "#52c41a" : parseFloat(record.avgRating) >= 3 ? "#1890ff" : "#faad14"}
					size="small"
				/>
			),
		},
	];

	if (!isLogged) {
		return <Navigate to={"/admin/auth/login"} replace={true} />;
	}

	const uniqueCategories = ["All", ...new Set(skillsData.map((s) => s.skillCategory))].filter(Boolean);

	return (
		<div>
			<PageTitle title="Back" />
			<div style={{ padding: "20px" }}>
				<Card>
					<div className="text-center mb-4">
						<Title level={2}>Workforce Skills Analysis</Title>
						<Text type="secondary">Comprehensive analysis of employee skills and capabilities</Text>
					</div>

					{/* Filter */}
					<Space className="mb-4">
						<Select
							value={selectedCategory}
							onChange={setSelectedCategory}
							style={{ width: 200 }}
							placeholder="Filter by Category"
						>
							{uniqueCategories.map((category) => (
								<Option key={category} value={category}>
									{category}
								</Option>
							))}
						</Select>
					</Space>

					{analysis && (
						<>
							{/* Key Metrics */}
							<Row gutter={16} className="mb-4">
								<Col span={6}>
									<Card>
										<Statistic
											title="Total Skills"
											value={analysis.totalSkills}
											prefix={<BookOutlined />}
										/>
									</Card>
								</Col>
								<Col span={6}>
									<Card>
										<Statistic
											title="Avg Skill Rating"
											value={analysis.avgRating}
											suffix="/ 5"
											prefix={<TrophyOutlined />}
											valueStyle={{ color: "#3f8600" }}
										/>
									</Card>
								</Col>
								<Col span={6}>
									<Card>
										<Statistic
											title="Skills Coverage"
											value={analysis.skillsCoverage}
											suffix="%"
											prefix={<UserOutlined />}
											valueStyle={{ color: "#1890ff" }}
										/>
									</Card>
								</Col>
								<Col span={6}>
									<Card>
										<Statistic
											title="Certification Rate"
											value={analysis.certificationRate}
											suffix="%"
											prefix={<FileTextOutlined />}
											valueStyle={{ color: "#722ed1" }}
										/>
									</Card>
								</Col>
							</Row>

							{/* Proficiency Distribution */}
							<Card title="Proficiency Level Distribution" className="mb-4">
								<Row gutter={16}>
									{Object.keys(analysis.proficiencyDistribution).map((level) => {
										const count = analysis.proficiencyDistribution[level];
										const total = Object.values(analysis.proficiencyDistribution).reduce((a, b) => a + b, 0);
										const percentage = total > 0 ? (count / total) * 100 : 0;
										const color =
											level === "Expert"
												? "#52c41a"
												: level === "Advanced"
												? "#1890ff"
												: level === "Intermediate"
												? "#faad14"
												: "#ff4d4f";
										return (
											<Col span={6} key={level}>
												<div style={{ textAlign: "center" }}>
													<Text strong>{level}</Text>
													<Progress
														type="circle"
														percent={percentage}
														format={() => count}
														size={100}
														strokeColor={color}
													/>
												</div>
											</Col>
										);
									})}
								</Row>
							</Card>

							<Row gutter={16}>
								{/* Most Common Skills */}
								<Col span={12}>
									<Card
										title={
											<Space>
												<RiseOutlined />
												Most Common Skills
											</Space>
										}
										className="mb-4"
									>
										<Table
											columns={mostCommonSkillsColumns}
											dataSource={analysis.mostCommonSkills}
											rowKey="skillName"
											pagination={false}
											size="small"
										/>
									</Card>
								</Col>

								{/* Top Skilled Employees */}
								<Col span={12}>
									<Card
										title={
											<Space>
												<TrophyOutlined />
												Top Skilled Employees
											</Space>
										}
										className="mb-4"
									>
										<Table
											columns={topSkilledColumns}
											dataSource={analysis.topSkilledEmployees}
											rowKey="employeeId"
											pagination={false}
											size="small"
										/>
									</Card>
								</Col>
							</Row>

							{/* Skills by Category */}
							<Card title="Skills Distribution by Category" className="mb-4">
								<Table
									columns={categoryColumns}
									dataSource={analysis.categoryBreakdown}
									rowKey="category"
									pagination={false}
								/>
							</Card>

							{/* Skills Gaps */}
							{analysis.skillsGaps.length > 0 && (
								<Card
									title={
										<Space>
											<WarningOutlined />
											Skills Gaps (Needs Improvement)
										</Space>
									}
									className="mb-4"
								>
									<Alert
										message="Skills Requiring Attention"
										description={`${analysis.skillsGaps.length} skills identified with ratings below 3.0. Consider training or development plans.`}
										type="warning"
										showIcon
										className="mb-3"
									/>
									<Table
										columns={skillsGapsColumns}
										dataSource={analysis.skillsGaps}
										rowKey={(record, index) => `${record.employeeName}-${record.skillName}-${index}`}
										pagination={false}
										size="small"
									/>
								</Card>
							)}

							{/* Monthly Growth */}
							<Card title="Skills Growth Trend (Last 6 Months)" className="mb-4">
								<Row gutter={16}>
									{analysis.monthlyGrowth.map((trend, index) => (
										<Col span={4} key={index}>
											<Card size="small">
												<Statistic
													title={trend.month}
													value={trend.count}
													suffix="skills"
													valueStyle={{ color: "#1890ff" }}
												/>
											</Card>
										</Col>
									))}
								</Row>
							</Card>

							{/* Quick Insights */}
							<Row gutter={16}>
								<Col span={12}>
									<Card title="Certification Statistics">
										<Space direction="vertical" style={{ width: "100%" }}>
											<Statistic
												title="Certified Skills"
												value={analysis.certifiedSkills}
												suffix={`/ ${analysis.totalSkills}`}
												valueStyle={{ color: "#722ed1" }}
											/>
											<Progress
												percent={parseFloat(analysis.certificationRate)}
												status="active"
												strokeColor="#722ed1"
											/>
										</Space>
									</Card>
								</Col>
								<Col span={12}>
									<Card title="Quick Insights">
										<Space direction="vertical" style={{ width: "100%" }}>
											<Alert
												message="Skills Coverage"
												description={`${analysis.uniqueEmployees} out of ${analysis.totalEmployees} employees have recorded skills (${analysis.skillsCoverage}%)`}
												type={parseFloat(analysis.skillsCoverage) >= 80 ? "success" : "info"}
												showIcon
											/>
											<Alert
												message="Average Skill Rating"
												description={`Overall average skill rating is ${analysis.avgRating}/5 across all skills`}
												type={parseFloat(analysis.avgRating) >= 4 ? "success" : parseFloat(analysis.avgRating) >= 3 ? "info" : "warning"}
												showIcon
											/>
										</Space>
									</Card>
								</Col>
							</Row>
						</>
					)}

					{!analysis && (
						<Alert
							message="No Data Available"
							description="No skills data found for analysis. Record some employee skills first."
							type="info"
							showIcon
						/>
					)}
				</Card>
			</div>
		</div>
	);
};

export default WorkforceSkillsAnalysis;

